import React, { useState, useEffect } from 'react';
import { Modal } from '../admin/Modal';
import { CheckCircle, Flag, AlertTriangle } from 'lucide-react';

/**
 * ActionItem interface - matches backend data model
 */
export interface ActionItem {
  id: string;
  title: string;
  notes: string | null;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  owner_type: 'coach' | 'client';
  due_date: string | null;
  status: 'not_started' | 'in_progress' | 'completed';
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface ClientActionItemModalProps {
  item: ActionItem;
  onClose: () => void;
  onSave: () => void;
}

/**
 * CLIENT ACTION ITEM MODAL
 *
 * Read-only view of action item details for clients.
 * Based on staff ActionItemModal but with limited editability.
 *
 * Read-only fields: Title, Notes, Due Date, Priority
 * Hidden fields: Assigned To (not relevant for client view)
 * Editable: Status (so client can mark in_progress or completed)
 *
 * No delete functionality - clients can only view and update status.
 */
export const ClientActionItemModal: React.FC<ClientActionItemModalProps> = ({
  item,
  onClose,
  onSave
}) => {
  const isCompleted = item.status === 'completed';

  // Status and due date are editable
  const [status, setStatus] = useState<'not_started' | 'in_progress' | 'completed'>(item.status);
  const [dueDate, setDueDate] = useState(item.due_date || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setStatus(item.status);
    setDueDate(item.due_date || '');
    setHasChanges(false);
  }, [item]);

  const handleStatusChange = (newStatus: 'not_started' | 'in_progress' | 'completed') => {
    setStatus(newStatus);
    setHasChanges(newStatus !== item.status || dueDate !== (item.due_date || ''));
  };

  const handleDueDateChange = (newDate: string) => {
    setDueDate(newDate);
    setHasChanges(status !== item.status || newDate !== (item.due_date || ''));
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/my/action-items/${item.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, due_date: dueDate || null })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update action item');
      }

      onSave();
    } catch (err) {
      console.error('Error updating action item:', err);
      setError(err instanceof Error ? err.message : 'Failed to update action item');
    } finally {
      setLoading(false);
    }
  };

  // Priority display config
  const priorityConfig = {
    urgent: { label: 'URGENT', color: '#a855f7' },
    high: { label: 'High', color: '#ef4444' },
    medium: { label: 'Medium', color: '#eab308' },
    low: { label: 'Low', color: '#3b82f6' }
  };

  const priorityInfo = priorityConfig[item.priority];

  // Check if overdue
  const isOverdue = () => {
    if (!item.due_date || item.status === 'completed') return false;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const due = new Date(item.due_date);
    due.setHours(0, 0, 0, 0);
    return due < now;
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Action Item Details"
      size="medium"
      variant="info"
      hasUnsavedChanges={hasChanges}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
          {hasChanges && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </>
      }
    >
      <div className="client-action-item-view">
        {error && (
          <div className="form-error">
            {error}
          </div>
        )}

        {/* Completed status banner */}
        {isCompleted && (
          <div className="completed-banner">
            <CheckCircle size={16} />
            <span>Completed {item.completed_at ? new Date(item.completed_at).toLocaleDateString() : ''}</span>
          </div>
        )}

        {/* Overdue warning */}
        {isOverdue() && (
          <div className="overdue-banner">
            <AlertTriangle size={16} />
            <span>This item is overdue</span>
          </div>
        )}

        {/* Title (read-only, full width) */}
        <div className="detail-section">
          <label>Title</label>
          <div className="detail-value title-value">{item.title}</div>
        </div>

        {/* Notes (read-only, always visible like staff modal) */}
        <div className="detail-section">
          <label>Notes</label>
          <div className="detail-value notes-value">
            {item.notes || <span className="placeholder-text">No notes added</span>}
          </div>
        </div>

        {/* Due Date, Priority, Status - all on one row (matching staff layout) */}
        <div className="detail-row three-col">
          <div className="detail-section">
            <label>Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => handleDueDateChange(e.target.value)}
              disabled={loading}
              className={isOverdue() ? 'date-overdue' : ''}
            />
          </div>

          <div className="detail-section">
            <label>Priority</label>
            <div className="detail-value priority-value">
              <Flag size={14} style={{ color: priorityInfo.color }} />
              <span style={{ color: priorityInfo.color }}>{priorityInfo.label}</span>
            </div>
          </div>

          <div className="detail-section">
            <label>Status</label>
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value as 'not_started' | 'in_progress' | 'completed')}
              disabled={loading}
              className={status === 'completed' ? 'status-completed' : ''}
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      <style>{`
        .client-action-item-view {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-error {
          background: rgba(220, 38, 38, 0.1);
          border: 1px solid var(--accent-red);
          color: var(--accent-red);
          padding: 12px;
          border-radius: var(--radius-medium);
          font-size: 14px;
        }

        .completed-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(52, 199, 89, 0.1);
          border: 1px solid var(--accent-green);
          color: var(--accent-green);
          padding: 12px;
          border-radius: var(--radius-medium);
          font-size: 14px;
          font-weight: 500;
        }

        .overdue-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid var(--accent-red);
          color: var(--accent-red);
          padding: 12px;
          border-radius: var(--radius-medium);
          font-size: 14px;
          font-weight: 500;
        }

        .detail-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .detail-section label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .detail-value {
          font-size: 14px;
          color: var(--text-primary);
          padding: 10px 12px;
          background: var(--bg-secondary);
          border-radius: var(--radius-medium);
          border: 1px solid var(--border-primary);
        }

        .title-value {
          font-weight: 600;
          font-size: 16px;
        }

        .notes-value {
          white-space: pre-wrap;
          line-height: 1.5;
          min-height: 80px;
          resize: vertical;
        }

        .date-value,
        .priority-value {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 42px; /* Match height of select/input fields */
        }

        .date-value.overdue {
          color: var(--accent-red);
          border-color: var(--accent-red);
          background: rgba(239, 68, 68, 0.05);
        }

        .detail-row {
          display: flex;
          gap: 16px;
        }

        .detail-row .detail-section {
          flex: 1;
        }

        .detail-row.three-col {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
        }

        .placeholder-text {
          color: var(--text-tertiary);
          font-style: italic;
        }

        .detail-section select {
          padding: 10px 12px;
          font-size: 14px;
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          background: var(--bg-secondary);
          color: var(--text-primary);
          cursor: pointer;
          transition: border-color 0.2s ease;
          width: 100%;
        }

        .detail-section select:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .detail-section select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-completed {
          background: rgba(52, 199, 89, 0.15) !important;
          border-color: var(--accent-green) !important;
          color: var(--accent-green) !important;
        }

        .detail-section input[type="date"] {
          padding: 10px 12px;
          font-size: 14px;
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          background: var(--bg-secondary);
          color: var(--text-primary);
          transition: border-color 0.2s ease;
          width: 100%;
        }

        .detail-section input[type="date"]:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .detail-section input[type="date"]:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .detail-section input[type="date"].date-overdue {
          border-color: var(--accent-red);
          background: rgba(239, 68, 68, 0.1);
        }
      `}</style>
    </Modal>
  );
};

export default ClientActionItemModal;
